import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import styles from "./RegistrationPage.module.css";

const COUNTRY_CODES = [
    { code: "+1", name: "USA/Canada" },
    { code: "+44", name: "UK" },
    { code: "+61", name: "Australia" },
    { code: "+971", name: "UAE" },
    { code: "+65", name: "Singapore" },
    { code: "+49", name: "Germany" },
    { code: "+33", name: "France" },
    { code: "+81", name: "Japan" },
    { code: "+86", name: "China" },
    { code: "+7", name: "Russia" },
    { code: "+234", name: "Nigeria" },
    { code: "+27", name: "South Africa" },
    { code: "+94", name: "Sri Lanka" },
    { code: "+977", name: "Nepal" },
    { code: "+880", name: "Bangladesh" },
];

const validationSchema = Yup.object().shape({
    name: Yup.string()
        .trim()
        .min(3, "Name must be at least 3 characters")
        .max(50, "Name is too long")
        .matches(/^[a-zA-Z\s.]*$/, "Name should not contain numbers or special characters")
        .required("Full name is required"),

    email: Yup.string()
        .trim()
        .email("Please enter a valid email address")
        .required("Email is required"),

    delegateType: Yup.string()
        .oneOf(["indian", "foreign"], "Please select a delegate type")
        .required("Please select delegate type"),

    countryCode: Yup.string().required("Country code is required"),

    mobile: Yup.string()
        .required("Mobile number is required")
        .when("delegateType", {
            is: "indian",
            then: (schema) =>
                schema.matches(/^[6-9]\d{9}$/, "Enter a valid 10-digit Indian mobile number"),
            otherwise: (schema) =>
                schema.matches(/^\d{6,15}$/, "Enter a valid mobile number (6-15 digits)"),
        }),
});

/* ---------- Small inline icons (no extra dependency) ---------- */
const UserIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M12 12c2.7 0 4.9-2.2 4.9-4.9S14.7 2.2 12 2.2 7.1 4.4 7.1 7.1 9.3 12 12 12zm0 2.5c-3.3 0-9.8 1.6-9.8 4.9v2.4h19.6v-2.4c0-3.3-6.5-4.9-9.8-4.9z" />
    </svg>
);
const MailIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
    </svg>
);
const PhoneIcon = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
        <path d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.4 0 .8-.2 1L6.6 10.8z" />
    </svg>
);
const IndiaFlagIcon = () => (
    <svg viewBox="0 0 36 24" width="30" height="20">
        <rect width="36" height="8" fill="#FF9933" />
        <rect y="8" width="36" height="8" fill="#fff" />
        <rect y="16" width="36" height="8" fill="#138808" />
        <circle cx="18" cy="12" r="3" fill="none" stroke="#000080" strokeWidth="0.5" />
    </svg>
);
const GlobeIcon = () => (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="currentColor">
        <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm7.9 9h-3.2c-.1-2-.5-3.8-1.2-5.2A8 8 0 0 1 19.9 11zM12 4.1c.9 1.1 1.7 3.1 1.9 5.9h-3.8c.2-2.8 1-4.8 1.9-5.9zM4.1 13h3.2c.1 2 .5 3.8 1.2 5.2A8 8 0 0 1 4.1 13zm3.2-2H4.1a8 8 0 0 1 4.4-5.2C7.8 7.2 7.4 9 7.3 11zM12 19.9c-.9-1.1-1.7-3.1-1.9-5.9h3.8c-.2 2.8-1 4.8-1.9 5.9zm2.1-7.9h-4.2c.1-2 .5-3.7 1.1-5h2c.6 1.3 1 3 1.1 5zm.4 2c-.1 2-.5 3.8-1.2 5.2a8 8 0 0 0 4.4-5.2h-3.2z" />
    </svg>
);

const RegistrationPage = () => {
    const formik = useFormik({
        initialValues: {
            name: "",
            email: "",
            delegateType: "",
            countryCode: "+91",
            mobile: "",
        },
        validationSchema,
        onSubmit: (values, { resetForm, setSubmitting }) => {
            // TODO: Replace this block with your actual API call.
            // Example:
            // await axios.post("/api/register", values);

            // console.log("Form Submitted:", values);

            Swal.fire({
                icon: "info",
                title: "We're Glad You're Interested! 💜",
                html: `
                        <div style="line-height: 1.6;">
                            <p style="margin: 0; font-size: 16px; color: #334155;">
                                Thank you, <strong style="color: #7c3aed;">
                                    ${values.name}
                                </strong>, for your interest in our Summit!
                            </p>

                            <p style="margin: 10px 0 0; font-size: 14px; color: #64748b;">
                                We're preparing something special and will open
                                registrations shortly.
                            </p>

                            <p style="margin: 14px 0 0; font-size: 13px; color: #94a3b8;">
                                Keep an eye out for the official registration opening. ✨
                            </p>
                        </div>
                    `,
                confirmButtonText: "I'll Keep an Eye Out! 👀",
                confirmButtonColor: "#7c3aed",
            });

            resetForm();
            setSubmitting(false);
        },
    });

    const handleDelegateChange = (value) => {
        formik
            .setValues({
                ...formik.values,
                delegateType: value,
                countryCode: value === "indian" ? "+91" : "+1",
                mobile: "",
            })
            .then(() => {
                formik.setFieldTouched("delegateType", true, true);
            });
    };

    return (
        <div className={styles.pageWrapper}>
            <div className="container position-relative">
                <div className="row justify-content-center">
                    <div className="col-12 col-md-10 col-lg-7">
                        <div className={styles.formCard}>
                            <div className={styles.cardTopAccent}></div>

                            <div className="p-4 p-md-5">
                                <div className={styles.headerBlock}>
                                    <div className={styles.badge}>✦ Event Registration ✦</div>
                                    <h2 className={styles.formTitle}>Join Us at the Summit</h2>
                                    <p className={styles.formSubtitle}>
                                        Fill in your details below — it only takes a minute.
                                    </p>
                                </div>

                                <form onSubmit={formik.handleSubmit} noValidate>
                                    {/* Full Name */}
                                    <div className={`${styles.fieldGroup} mb-4 text-start`}>
                                        <label htmlFor="name" className={styles.fieldLabel}>
                                            Full Name <span className={styles.required}>*</span>
                                        </label>
                                        <div
                                            className={`${styles.inputWrap} ${formik.touched.name && formik.errors.name ? styles.inputError : ""
                                                }`}
                                        >
                                            <span className={styles.inputIcon}>
                                                <UserIcon />
                                            </span>
                                            <input
                                                type="text"
                                                id="name"
                                                name="name"
                                                className={styles.styledInput}
                                                placeholder="Enter your full name"
                                                value={formik.values.name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                        {formik.touched.name && formik.errors.name && (
                                            <div className={styles.errorText}>{formik.errors.name}</div>
                                        )}
                                    </div>

                                    {/* Email */}
                                    <div className={`${styles.fieldGroup} mb-4 text-start`}>
                                        <label htmlFor="email" className={styles.fieldLabel}>
                                            Email Address <span className={styles.required}>*</span>
                                        </label>
                                        <div
                                            className={`${styles.inputWrap} ${formik.touched.email && formik.errors.email ? styles.inputError : ""
                                                }`}
                                        >
                                            <span className={styles.inputIcon}>
                                                <MailIcon />
                                            </span>
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                className={styles.styledInput}
                                                placeholder="you@example.com"
                                                value={formik.values.email}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            />
                                        </div>
                                        {formik.touched.email && formik.errors.email && (
                                            <div className={styles.errorText}>{formik.errors.email}</div>
                                        )}
                                    </div>

                                    {/* Delegate Type - selectable cards */}
                                    <div className={`${styles.fieldGroup} mb-4`}>
                                        <label className={styles.fieldLabel}>
                                            Delegate Type <span className={styles.required}>*</span>
                                        </label>

                                        <div className="row g-3">
                                            <div className="col-6">
                                                <div
                                                    className={`${styles.delegateCard} ${formik.values.delegateType === "indian" ? styles.delegateCardActive : ""
                                                        }`}
                                                    onClick={() => handleDelegateChange("indian")}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <div className={styles.delegateIcon}>
                                                        <IndiaFlagIcon />
                                                    </div>
                                                    <div className={styles.delegateLabel}>Indian Delegate</div>
                                                    {formik.values.delegateType === "indian" && (
                                                        <div className={styles.checkBadge}>✓</div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="col-6">
                                                <div
                                                    className={`${styles.delegateCard} ${formik.values.delegateType === "foreign" ? styles.delegateCardActive : ""
                                                        }`}
                                                    onClick={() => handleDelegateChange("foreign")}
                                                    role="button"
                                                    tabIndex={0}
                                                >
                                                    <div className={styles.delegateIcon}>
                                                        <GlobeIcon />
                                                    </div>
                                                    <div className={styles.delegateLabel}>Foreign Delegate</div>
                                                    {formik.values.delegateType === "foreign" && (
                                                        <div className={styles.checkBadge}>✓</div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {formik.touched.delegateType && formik.errors.delegateType && (
                                            <div className={styles.errorText}>{formik.errors.delegateType}</div>
                                        )}
                                    </div>

                                    {/* Mobile Number with Country Code */}
                                    <div className={`${styles.fieldGroup} mb-4`}>
                                        <label htmlFor="mobile" className={styles.fieldLabel}>
                                            Mobile Number <span className={styles.required}>*</span>
                                        </label>
                                        <div className="row g-2">
                                            <div className="col-5 col-sm-4">
                                                <div
                                                    className={`${styles.inputWrap} ${styles.selectWrap} ${formik.values.delegateType === "indian" ? styles.disabledWrap : ""
                                                        }`}
                                                >
                                                    <select
                                                        id="countryCode"
                                                        name="countryCode"
                                                        className={styles.styledSelect}
                                                        value={formik.values.countryCode}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                        disabled={formik.values.delegateType === "indian"}
                                                    >
                                                        {formik.values.delegateType === "indian" ? (
                                                            <option value="+91">🇮🇳 +91</option>
                                                        ) : (
                                                            COUNTRY_CODES.map((c) => (
                                                                <option key={c.code} value={c.code}>
                                                                    {c.code} {c.name}
                                                                </option>
                                                            ))
                                                        )}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="col-7 col-sm-8">
                                                <div
                                                    className={`${styles.inputWrap} ${formik.touched.mobile && formik.errors.mobile ? styles.inputError : ""
                                                        }`}
                                                >
                                                    <span className={styles.inputIcon}>
                                                        <PhoneIcon />
                                                    </span>
                                                    <input
                                                        type="text"
                                                        id="mobile"
                                                        name="mobile"
                                                        inputMode="numeric"
                                                        className={styles.styledInput}
                                                        placeholder={
                                                            formik.values.delegateType === "indian"
                                                                ? "10-digit mobile number"
                                                                : "Mobile number"
                                                        }
                                                        value={formik.values.mobile}
                                                        onChange={formik.handleChange}
                                                        onBlur={formik.handleBlur}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        {formik.touched.mobile && formik.errors.mobile && (
                                            <div className={styles.errorText}>{formik.errors.mobile}</div>
                                        )}
                                    </div>

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        className={styles.submitBtn}
                                        disabled={formik.isSubmitting}
                                    >
                                        <span>{formik.isSubmitting ? "Submitting..." : "Register Now"}</span>
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RegistrationPage;